import type {
	Photos
} from "~/types/typeDefinitions";
import type {
	ImageKitFile
} from "~/types/imageKitAPI";

const CACHE_TTL_MS = 60 * 60 * 1000;

const getCached = <T>( key: string ): T | null => {
	try {
		const raw = localStorage.getItem( key );
		if ( !raw ) return null;

		const { timestamp, data } = JSON.parse( raw );
		if ( Date.now() - timestamp > CACHE_TTL_MS ) return null;

		return data as T;
	} catch {
		return null;
	}
};

const setCached = <T>( key: string, data: T ): void => {
	try {
		localStorage.setItem( key, JSON.stringify( { timestamp: Date.now(), data } ) );
	} catch {
		// localStorage unavailable (private browsing, quota exceeded, etc.) - skip caching
	}
};

export const useGetImages = () => {
	/**
	 * Fetches images from the ImageKit API, serving a localStorage-cached
	 * response when one exists and hasn't exceeded CACHE_TTL_MS.
	 *
	 * @async
	 * @function fetchImages
	 * @param {Record<string, string>} body - The POST body sent to `/api/imageKit`.
	 * @returns {Promise<ImageKitFile[]>} The (possibly cached) ImageKit file list.
	 */
	const fetchImages = async ( body: Record<string, string> ): Promise<ImageKitFile[]> => {
		const cacheKey = `imagekit-cache:${ JSON.stringify( body ) }`;
		const cached = getCached<ImageKitFile[]>( cacheKey );

		if ( cached ) return cached;
		// if ( cached && cacheKey !== `imagekit-cache:${ JSON.stringify( { path: "weddingParty", fileType: "image" } ) }` ) return cached;

		const data = await $fetch<ImageKitFile[]>( "/api/imageKit", {
			method: "POST",
			body
		} );

		setCached( cacheKey, data || [] );
		return data || [];
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit/getImages` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getPhotoPageImages
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getPhotoPageImages = async (): Promise<Photos[]> => {
		try {
			const data = await fetchImages( {
				path: "photoPage",
				fileType: "image"
			} );

			return parseImageData( data );
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return [];
		}
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getHomePageImage
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getHomePageImage = async (): Promise<( Photos & { index: number } )[]> => {
		try {
			const data = await fetchImages( {
				path: "home",
				fileType: "image"
			} );

			return parseImageData( data );
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return [];
		}
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getOurStoryPageImages
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getOurStoryPageImages = async (): Promise<( Photos & { index: number } )[]> => {
		try {
			const data = await fetchImages( {
				path: "ourStory",
				fileType: "image",
				sort: "ASC_NAME"
			} );
			return parseImageData( data );
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return [];
		}
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getOurStoryPageImages
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getThingsToDoPageImages = async (): Promise<( Photos & { index: number } )[]> => {
		try {
			const data = await fetchImages( {
				path: "thingsToDo",
				fileType: "image"
			} );
			return parseImageData( data );
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return [];
		}
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getWeddingPartyPhotos
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getWeddingPartyPhotos = async (): Promise<( Photos & { index: number } )[]> => {
		try {
			const data = await fetchImages( {
				path: "weddingParty",
				fileType: "image",
				sort: "DESC_NAME"
			} );
			return parseImageData( data );
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return [];
		}
	};

	/**
	 * Fetches photos from the ImageKit API and populates the `photos` array.
	 *
	 * - Sends a GET request to the `/api/imageKit` endpoint with query parameters for image files.
	 * - Maps the response data to the `IPhotos` interface and adds an `index` property to each photo.
	 * - Updates the `photos` array with the processed photo data.
	 *
	 * @async
	 * @function getInspirationImages
	 * @returns {Promise<(Photos & { index: number })[]>} A promise that resolves when the photos are successfully fetched and processed.
	 */
	const getInspirationImages = async (): Promise<{ suit: ( Photos & { index: number } )[]; dress: ( Photos & { index: number } )[] }> => {
		try {
			const suitDataPromise = fetchImages( {
				path: "suitInspiration",
				fileType: "image"
			} );
			const dressDataPromise = fetchImages( {
				path: "dressInspiration",
				fileType: "image"
			} );

			const [ suitData, dressData ] = await Promise.all( [ suitDataPromise, dressDataPromise ] );
			const suitImages = parseImageData( suitData );
			const dressImages = parseImageData( dressData );

			return {
				suit: suitImages,
				dress: dressImages
			};
		} catch ( error ) {
			console.error( `Error fetching images from ImageKit API: ${ error }` );
			return {
				suit: [],
				dress: []
			};
		}
	};

	const parseImageData = ( imageData: ImageKitFile[] ): Photos[] => {
		const images: ( Photos & { index: number } )[] = [];
		imageData.forEach( ( image: ImageKitFile, index: number ) => {
			const currentImage: Photos & { index: number } = {
				src: `${ image.filePath }?updatedAt=${ encodeURIComponent( image.updatedAt ) }`,
				alt: image.name,
				caption: "",
				date: new Date( image.createdAt ),
				index: index
			};
			images.push( currentImage );
		} );
		return images;
	};

	return {
		getPhotoPageImages,
		getHomePageImage,
		getOurStoryPageImages,
		getThingsToDoPageImages,
		getInspirationImages,
		getWeddingPartyPhotos
	};
};